/**
 * 结算面板（结局批 2026-09-29，设计=docs/实现说明.md §3.4g + 迁存章 A5 §三）
 * 视觉：结局定格做成「谢幕纪念卡」——奖状式双线金边证书 + 卡上直排总结文案；其余四节走 mint 便签组件。
 * 数据唯一来源 = MetaData.lastCareerSummary（endGame 时写入，删档/刷新安全）；
 * meta 缺失时回落现读内存（仅兜旧流程残留）。
 */
import type { GameApp } from '../../app';
import { GameState } from '../../systems/GameState';
import { gameManager } from '../../core/GameManager';
import { rankSystem } from '../../systems/RankSystem';
import { saveSystem } from '../../systems/SaveSystem';
import { Utils } from '../../core/Utils';
import { ScriptGenre, AwardHonor } from '../../data/types';
import { endedByLabel } from '../../data/tables/Endings';
import { createDiv, createSpan, createButton, createMintBlock } from '../ui';

const GENRE_LABELS: Record<ScriptGenre, string> = {
    movie: '电影', tv: '电视剧', web: '网剧', short: '短剧', variety: '综艺', other: '其他',
};

/** 奖杯素材（web/public/trophies/*.webp；320×320，约 15KB/张，2026-10-18 压 webp） */
const TROPHY_SRC: Record<string, string> = {
    jingshu: 'trophies/金枢奖奖杯.webp',
    xinghan: 'trophies/星汉奖奖杯.webp',
};

export class GameOverPanel {
    readonly element: HTMLElement;

    private inner: HTMLElement;

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root');
        this.inner = createDiv('over-inner');
        panel.appendChild(this.inner);
        this.element = panel;
    }

    refresh(): void {
        const summary = saveSystem.loadMeta().lastCareerSummary;
        this.inner.replaceChildren();
        if (summary) {
            this.renderSummary(summary);
        } else {
            this.renderFallback();
        }
    }

    /** 正常路径：谢幕纪念卡 + 四节便签 + 按钮 */
    private renderSummary(s: NonNullable<ReturnType<typeof saveSystem.loadMeta>['lastCareerSummary']>): void {
        // —— 谢幕纪念卡（① 结局定格）：奖状式双线证书，结局标题大字 + 信纸总结 ——
        const cert = createDiv('over-cert' + (s.endedBy === 'stage_fail' ? ' is-fail' : ''));
        cert.appendChild(createDiv('over-cert-tag', ['✦ 生 涯 纪 念 ✦']));
        cert.appendChild(createDiv('over-end-title', [s.title]));
        cert.appendChild(createDiv('over-cert-rule'));
        cert.appendChild(createDiv('over-end-meta', [s.endedBy === 'stage_fail'
            ? `止步于 ${s.rankName}`
            : `${endedByLabel(s.endedBy)} · ${s.stats.age} 岁谢幕`]));
        cert.appendChild(createDiv('over-summary', [s.summaryText]));
        this.inner.appendChild(cert);

        // —— ② 生涯荣誉 ——
        const sec2 = createMintBlock('生涯荣誉');
        const trio = createDiv('over-trio');
        const trioCells: { k: string; v: string }[] = [
            { k: '最终咖位', v: s.rankName },
            { k: '口碑', v: String(s.reputation) },
            { k: '名气', v: String(s.fame) },
            { k: '粉丝', v: Utils.formatCountCN(s.fans) },
        ];
        trioCells.forEach(c => {
            const cell = createDiv('cell');
            cell.appendChild(createDiv('num', [c.v]));
            cell.appendChild(createSpan('k', c.k));
            trio.appendChild(cell);
        });
        sec2.appendChild(trio);
        sec2.appendChild(createDiv('over-award-line',
            [`🏆 获奖 ${s.awards.won} · 提名 ${s.awards.nominated}`]));
        this.renderHonors(sec2, s.honors ?? []);
        this.inner.appendChild(sec2);

        // —— ②½ 生涯代表作（2026-09-30 系数排行+展示强化；三改=全部同宽纵排，修双列与通栏卡边缘不齐）——
        if (s.topWorks && s.topWorks.length > 0) {
            const secSig = createMintBlock('生涯代表作');
            secSig.classList.add('over-sec');
            const podium = createDiv('over-podium');
            s.topWorks.forEach((w, i) => {
                const champion = i === 0;
                const card = createDiv(champion ? 'over-pod-card champion' : 'over-pod-card');
                const head = createDiv('pod-head');
                const rankWrap = createDiv('pod-rank');
                rankWrap.appendChild(createSpan(undefined, champion ? '代表作' : `第${i + 1}`));
                head.appendChild(rankWrap);
                head.appendChild(createSpan('pod-heat', `热度 ${w.heat}`));
                card.appendChild(head);
                const nameRow = createDiv('pod-name');
                nameRow.appendChild(createSpan('pod-name-text', `《${w.workName}》`));
                if (w.boom) nameRow.appendChild(createSpan('heatchart-boom', '爆'));
                card.appendChild(nameRow);
                const meta = createDiv('pod-meta');
                const bits = [w.roleName, w.roleType, champion ? GENRE_LABELS[w.genre] : null].filter(Boolean).join(' · ');
                meta.appendChild(createSpan('', `${bits} · `));
                meta.appendChild(createSpan('pod-score', `${w.score.toFixed(1)} 分`));
                card.appendChild(meta);
                podium.appendChild(card);
            });
            secSig.appendChild(podium);
            this.inner.appendChild(secSig);
        }

        // —— ③ 数字人生 ——
        const sec3 = createMintBlock('数字人生');
        const facts = createDiv('over-facts');
        const genreText = (Object.entries(s.stats.worksByGenre) as [ScriptGenre, number][])
            .filter(([, n]) => n > 0)
            .map(([g, n]) => `${GENRE_LABELS[g]} ${n}`)
            .join(' / ');
        const items: { k: string; v: string; wide?: boolean }[] = [
            { k: '生涯作品', v: genreText ? `${s.stats.works} 部（${genreText}）` : `${s.stats.works} 部`, wide: true },
            { k: '微博', v: `${s.stats.posts} 条 · 获赞 ${Utils.formatCountCN(s.stats.likes)}`, wide: true },
            { k: '累计上课', v: `${s.stats.courses} 节` },
            { k: '结识业内', v: `${s.stats.knownNpcs} 人` },
            { k: '挚友', v: `${s.stats.closeFriends} 人` },
            { k: '累计赚取', v: `¥${Utils.formatMoney(s.stats.totalMoney)}` },
        ];
        items.forEach(item => {
            const row = createDiv(item.wide ? 'over-fact over-fact--wide' : 'over-fact');
            row.appendChild(createSpan('k', item.k));
            row.appendChild(createSpan('v', item.v));
            facts.appendChild(row);
        });
        sec3.appendChild(facts);
        this.inner.appendChild(sec3);

        // —— ④ 生涯履历 ——
        const sec4 = createMintBlock('生涯履历');
        if (s.filmography.length === 0) {
            sec4.appendChild(createDiv('over-empty', ['这个周目没有留下播出的作品。']));
        } else {
            const list = createDiv('over-filmo');
            for (const f of s.filmography) {
                // 版式复用履历页同族 .career-item；类型走纯文字——.career-chip 的薄荷底坐在薄荷块上等于隐形
                const item = createDiv('career-item');
                const line1 = createDiv('career-line');
                line1.appendChild(createSpan('career-title', `《${f.workName}》`));
                line1.appendChild(createSpan('career-sub', GENRE_LABELS[f.genre]));
                if (f.boom) line1.appendChild(createSpan('heatchart-boom', '爆'));
                line1.appendChild(createSpan('career-nums', `评分 ${f.score.toFixed(1)}`));
                item.appendChild(line1);
                const line2 = createDiv('career-line');
                line2.appendChild(createSpan('career-sub', f.roleType));
                line2.appendChild(createSpan('career-when', `第${f.year + 1}年·${f.month}月`));
                item.appendChild(line2);
                list.appendChild(item);
            }
            sec4.appendChild(list);
        }
        this.inner.appendChild(sec4);

        // —— ⑤ 周目结算 ——
        const sec5 = createMintBlock('周目结算');
        if (s.newAchievements.length > 0) {
            for (const a of s.newAchievements) {
                const row = createDiv('over-ach-row');
                row.appendChild(createSpan('an', a.name));
                row.appendChild(createSpan('ap', `+${a.points} 点`));
                sec5.appendChild(row);
            }
            sec5.appendChild(createDiv('over-earned', [`合计获得 ${s.earnedPoints} 成就点`]));
        } else {
            sec5.appendChild(createDiv('over-empty', ['本周目没有新解锁的成就。']));
        }
        this.inner.appendChild(sec5);

        this.renderButtons();
    }

    /**
     * 获奖陈列（2026-10-18，样式见 `ui.css` 的 `.trophy-*`）：
     * 两列展位、按获奖年份从先往后（EndingSystem 快照时已排好）；无获奖＝整块不出现。
     */
    private renderHonors(host: HTMLElement, honors: AwardHonor[]): void {
        if (honors.length === 0) return;
        const section = createDiv('award-history-section');
        section.appendChild(createDiv('award-section-title', ['获奖陈列']));
        const cabinet = createDiv('trophy-cabinet');
        for (let i = 0; i < honors.length; i += 2) {
            const shelf = createDiv('trophy-shelf');
            for (const h of honors.slice(i, i + 2)) {
                const entry = createDiv('trophy-entry');
                entry.appendChild(createSpan('trophy-edition', `第${h.edition} 届`));
                const visual = createDiv('trophy-visual');
                const img = document.createElement('img');
                img.src = TROPHY_SRC[h.awardId] ?? TROPHY_SRC.jingshu;
                img.alt = '';
                visual.appendChild(img);
                entry.appendChild(visual);
                entry.appendChild(createDiv('trophy-award', [`${h.awardName}${h.categoryName}`]));
                entry.appendChild(createDiv('trophy-work-role',
                    [h.roleName ? `《${h.workName}》${h.roleName}` : `《${h.workName}》`]));
                shelf.appendChild(entry);
            }
            cabinet.appendChild(shelf);
        }
        section.appendChild(cabinet);
        host.appendChild(section);
    }

    /** 兜底路径：meta 无快照（旧流程残留）→ 现读内存渲染极简版 */
    private renderFallback(): void {
        const state = GameState.getInstance();
        const finalRank = state.player.rank;
        const rankName = rankSystem.getRankName(finalRank);

        const rankBlock = createMintBlock('最终咖位');
        // 咖位口径（2026-10-23 UI规范 §6）：玩家可见读数一律用咖位名，`Lv.` 只留控制台/测试
        rankBlock.appendChild(createDiv('week-sched-title', [rankName]));
        this.inner.appendChild(rankBlock);

        const grid = createDiv('summary-grid');
        const items: { k: string; v: string }[] = [
            { k: '年龄', v: `${state.player.age} 岁` },
            { k: '累计赚钱', v: `¥${state.getMoney()}` },
            { k: '完成通告', v: `${state.player.totalShotsCompleted} 部` },
            { k: '名气', v: `${state.player.fame}` },
        ];
        items.forEach(item => {
            const el = createDiv('summary-item');
            el.appendChild(createSpan('k', item.k));
            el.appendChild(createSpan('v', item.v));
            grid.appendChild(el);
        });
        this.inner.appendChild(grid);

        this.renderButtons();
    }

    /** 按钮区：成就商店 + 再来一周目（P0 简化=跳主菜单，2026-09-29 拍板） */
    private renderButtons(): void {
        const btnRow = createDiv('btn-row over-btns');
        const shopBtn = createButton('btn btn-ghost', '成就商店');
        shopBtn.addEventListener('click', () => this.app.show('achievementShop'));
        const againBtn = createButton('btn btn-primary', '再来一周目');
        againBtn.addEventListener('click', () => {
            gameManager.reset();
            this.app.show('mainMenu');
        });
        btnRow.appendChild(shopBtn);
        btnRow.appendChild(againBtn);
        this.inner.appendChild(btnRow);
    }

    destroy(): void {
        // 无外部事件监听
    }
}
